import { SupplierFactorDraftReviewState } from './supplierDraftContracts';
import { inclusiveEndDateFromExclusive } from './supplierFactorInterval';

// Presentation helpers shared by the draft-review surfaces (TemplateUploadPage
// result card, DraftsInboxPage, DraftReviewPage, ProposedFactorForm's
// currently-published-value hints).

export const DRAFT_REVIEW_STATE_LABELS = Object.freeze({
  [SupplierFactorDraftReviewState.Proposed]: 'Needs review',
  [SupplierFactorDraftReviewState.UnderReview]: 'Needs review',
  [SupplierFactorDraftReviewState.Discarded]: 'Discarded',
  [SupplierFactorDraftReviewState.Published]: 'Published',
});

const DRAFT_REVIEW_STATE_TONE = Object.freeze({
  [SupplierFactorDraftReviewState.Proposed]: 'text-amber-700 bg-amber-50',
  [SupplierFactorDraftReviewState.UnderReview]: 'text-amber-700 bg-amber-50',
  [SupplierFactorDraftReviewState.Discarded]: 'text-slate-500 bg-slate-100',
  [SupplierFactorDraftReviewState.Published]: 'text-emerald-700 bg-emerald-50',
});

export const draftReviewStateLabel = (state) => DRAFT_REVIEW_STATE_LABELS[state] || state;
export const draftReviewStateTone = (state) =>
  DRAFT_REVIEW_STATE_TONE[state] || 'text-slate-500 bg-slate-100';

// A draft is only editable/actionable (patch/discard/confirm) while pending
// review; discarded/published are terminal — the review page renders them
// read-only with a state banner instead of the form/footer.
export const isPendingDraftReviewState = (state) =>
  state === SupplierFactorDraftReviewState.Proposed ||
  state === SupplierFactorDraftReviewState.UnderReview;

const fmtDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
};

export const formatUploadDate = (value) => fmtDate(value);

// Half-open [startInclusive, endExclusive) → an inclusive display range
// (endExclusive - 1 day), matching how the rest of the module displays
// supplier factor intervals (RevisionHistoryModal, adapters.js).
export const formatDraftInterval = (interval) => {
  if (!interval?.startInclusive) return '—';
  const start = fmtDate(interval.startInclusive);
  const endIso = inclusiveEndDateFromExclusive(interval.endExclusive);
  if (!endIso) return start || '—';
  const end = fmtDate(endIso);
  return start === end ? start : `${start} → ${end}`;
};

// The two dates the half-open interval encodes, shown individually (the review
// form repeats them as read-only fields beside the values they scope).
export const formatDraftPeriodStart = (interval) => fmtDate(interval?.startInclusive) || '—';
export const formatDraftLastRecordDate = (interval) =>
  fmtDate(inclusiveEndDateFromExclusive(interval?.endExclusive)) || '—';

// The reporting year a draft publishes under — derived, never entered.
//
// Taken from the LAST RECORD DATE (the inclusive end), mirroring the ingestion
// that created these drafts: commsTemplateToDiagram.ts does
// `reportingYear = reportingPeriod.to.getFullYear()` and then derives the CBAM
// factor from it, so deriving it any other way here would publish a year the
// pipeline never would.
//
// The inclusive end matters: reading `endExclusive` directly is off by one for
// every period ending 31 Dec, whose endExclusive is 1 Jan of the NEXT year.
// Falls back to the start year when there is no usable end, and to null when
// the interval is missing entirely.
export const reportingYearFromInterval = (interval) => {
  const endIso = inclusiveEndDateFromExclusive(interval?.endExclusive);
  if (endIso) return Number(endIso.slice(0, 4));
  if (!interval?.startInclusive) return null;
  const start = new Date(interval.startInclusive);
  return Number.isNaN(start.getTime()) ? null : start.getUTCFullYear();
};

// Sentinel group id for drafts with NO lineage.sourceUploadId at all — a
// manually created draft (S10 backend change) that no upload ever produced.
// Distinct from a draft that DOES carry a real sourceUploadId whose upload
// record just isn't in the list (deleted / not yet loaded) — those land in
// their own per-id group with `upload: null` and keep the generic "Uploaded
// template" fallback copy. Exported so DraftsInboxPage can render this one
// bucket under a neutral "Manual drafts" heading instead of that fallback.
export const MANUAL_DRAFT_GROUP_ID = 'manual';

// Group drafts by the upload that produced them (draft.lineage.sourceUploadId),
// joined against the uploads list for the filename/date group header. Groups
// sort newest-upload-first; a draft whose upload record isn't in the list
// (deleted / not yet loaded) lands in its own group with `upload: null`; a
// draft with no sourceUploadId at all lands in the shared MANUAL_DRAFT_GROUP_ID
// bucket (also `upload: null`) so a manually created draft renders, grouped,
// instead of crashing or being silently dropped.
export const groupSupplierDraftsByUpload = (drafts, uploads) => {
  const uploadById = new Map((uploads || []).map((upload) => [upload.id, upload]));
  const groups = new Map();
  for (const draft of drafts || []) {
    const uploadId = draft.lineage?.sourceUploadId || MANUAL_DRAFT_GROUP_ID;
    if (!groups.has(uploadId)) {
      groups.set(uploadId, { uploadId, upload: uploadById.get(uploadId) || null, drafts: [] });
    }
    groups.get(uploadId).drafts.push(draft);
  }
  return [...groups.values()].sort((a, b) => {
    const ta = a.upload?.createdAt ? new Date(a.upload.createdAt).getTime() : 0;
    const tb = b.upload?.createdAt ? new Date(b.upload.createdAt).getTime() : 0;
    return tb - ta;
  });
};

// DraftsInboxPage's incremental reveal (owner: "waterfall rendering, no
// show-more or pages"): the initial paint shows at most this many draft
// cards across complete groups (groups stay in the newest-first order
// groupSupplierDraftsByUpload already produces); each "Show more" click adds
// one more chunk of budget.
export const INITIAL_DRAFT_REVEAL_BUDGET = 10;
export const DRAFT_REVEAL_CHUNK_SIZE = 10;

// Pure planner for DraftsInboxPage's client-side pagination. Given the
// (already newest-first) upload groups from groupSupplierDraftsByUpload, how
// many "Show more" chunks have been revealed, and which groups the viewer
// explicitly expanded via their own "Show all N drafts" affordance, decides
// which groups render and how many of each group's drafts are visible.
// Purely client-side: the drafts/uploads list endpoints already return
// everything, so this never fetches — it only decides what's on screen this
// render.
//
// Rules:
// - Complete groups are added in order until the budget is spent.
// - The group that straddles the budget is shown truncated to the remaining
//   budget (`isTruncated: true`, `hiddenCount` > 0) rather than disappearing
//   entirely — the caller renders a per-group "Show all N drafts" button for
//   it (N = group.drafts.length).
// - A group id in `expandedGroupIds` always renders in full, regardless of
//   budget (an explicit "Show all" click), and its full size still counts
//   against the budget for groups that come after it.
// - Groups beyond the spent budget aren't included at all this call —
//   `hasMore` tells the caller whether a "Show more" button is warranted.
export const planDraftGroupsReveal = (groups, options = {}) => {
  const {
    revealedChunks = 1,
    expandedGroupIds = [],
    initialBudget = INITIAL_DRAFT_REVEAL_BUDGET,
    chunkSize = DRAFT_REVEAL_CHUNK_SIZE,
  } = options;
  const expanded = new Set(expandedGroupIds);
  const extraChunks = Math.max(revealedChunks, 1) - 1;
  const budget = Math.max(initialBudget, 0) + extraChunks * Math.max(chunkSize, 0);

  const visibleGroups = [];
  let spent = 0;
  let hasMore = false;

  for (const group of groups || []) {
    const draftCount = group.drafts?.length || 0;
    if (draftCount === 0) continue;

    if (expanded.has(group.uploadId)) {
      visibleGroups.push({
        ...group,
        visibleDrafts: group.drafts,
        hiddenCount: 0,
        isTruncated: false,
      });
      spent += draftCount;
      continue;
    }

    if (spent >= budget) {
      hasMore = true;
      continue;
    }

    const remaining = budget - spent;
    if (draftCount <= remaining) {
      visibleGroups.push({
        ...group,
        visibleDrafts: group.drafts,
        hiddenCount: 0,
        isTruncated: false,
      });
      spent += draftCount;
    } else {
      const visibleDrafts = group.drafts.slice(0, remaining);
      visibleGroups.push({
        ...group,
        visibleDrafts,
        hiddenCount: draftCount - visibleDrafts.length,
        isTruncated: true,
      });
      spent = budget;
      hasMore = true;
    }
  }

  return { visibleGroups, hasMore, totalVisibleDraftCount: spent };
};

// draft.proposedFactor.emissions.{direct,indirect,total}, each defensively
// coerced to a number (or null) — the compact per-draft summary shown on
// DraftsInboxPage's draft cards.
export const draftEmissionsSummary = (draft) => {
  const emissions = draft?.proposedFactor?.emissions || {};
  const toNum = (v) => {
    const value = Number(v);
    return Number.isFinite(value) ? value : null;
  };
  return {
    direct: toNum(emissions.direct),
    indirect: toNum(emissions.indirect),
    total: toNum(emissions.total),
  };
};

// draft.proposedFactor.emissions.total, defensively coerced to a number.
export const draftTotalEf = (draft) => draftEmissionsSummary(draft).total;

/* -------------------------------------------------------- live "currently
   published" resolution (consumed by ProposedFactorForm's per-field
   currently-published hints, design decision #5) --------------------------- */

// Two intervals are the SAME reporting period (not merely overlapping) —
// millisecond comparison so ISO strings with/without trailing zeros still
// match.
const sameInterval = (a, b) => {
  if (!a?.startInclusive || !a?.endExclusive || !b?.startInclusive || !b?.endExclusive) {
    return false;
  }
  return (
    new Date(a.startInclusive).getTime() === new Date(b.startInclusive).getTime() &&
    new Date(a.endExclusive).getTime() === new Date(b.endExclusive).getTime()
  );
};

// A draft's fieldProvenance[].currentlyPublishedValue is a SNAPSHOT captured
// when the draft was created — it can go stale if the series is
// published/corrected again before the supplier reviews the draft. Find the
// matching series in a LIVE useSupplierFactors() list (identity = installation
// + CN code + product variant + production-method fingerprint, AND the
// draft's OWN reporting interval) so the provenance table can prefer the live
// value. Interval matters: a series' current revision is for exactly one
// period at a time — if the draft targets a DIFFERENT period than what's
// currently live (a correction for an older/newer interval), the live list
// value belongs to the wrong period and must not be shown as "currently
// published" for this proposal (see currentlyPublishedDisplayValue).
export const findMatchingSupplierFactor = (draft, items) => {
  const key = draft?.targetSeriesKey;
  if (!key) return null;
  return (
    (items || []).find((item) => {
      const seriesKey = item.seriesKey || {};
      const identityMatches =
        String(seriesKey.installationId) === String(key.installationId) &&
        seriesKey.cnCode === key.cnCode &&
        seriesKey.productVariant === key.productVariant &&
        seriesKey.methodFingerprint === key.methodFingerprint;
      return identityMatches && sameInterval(item.interval, draft?.interval);
    }) || null
  );
};

// Only `proposedFactor.*` field-provenance paths have a live equivalent on a
// SupplierFactorRevision (targetSeriesKey/interval/proposedCompany/
// proposedInstallation fields don't map onto a revision) — returns `undefined`
// for anything else so the caller falls back to the snapshot value.
export const resolveLiveProvenanceValue = (field, currentRevision) => {
  if (!currentRevision || typeof field !== 'string' || !field.startsWith('proposedFactor.')) {
    return undefined;
  }
  const path = field.slice('proposedFactor.'.length).split('.');
  let value = currentRevision;
  for (const key of path) {
    if (value == null) return null;
    value = value[key];
  }
  return value === undefined ? null : value;
};

// The display value for a provenance row's "Currently published" column: the
// live value when the field has one and an interval-matching series was
// found, and otherwise the draft's own server-projected snapshot. The
// snapshot is ALREADY the period-correct value (the backend resolves it via
// resolvePublishedSupplierFactor at the draft's own interval, not "whatever
// is current today") — including `null` for a genuinely new series — so
// falling back to it (rather than a hardcoded `null`) is correct whether
// there's no live series at all or the live series is for a different period.
export const currentlyPublishedDisplayValue = (row, matchedFactor) => {
  const live = resolveLiveProvenanceValue(row.field, matchedFactor?.currentRevision);
  return live !== undefined ? live : row.currentlyPublishedValue;
};
